import { useState, useEffect, useRef } from "react";
import { useTables } from "../report-builder";
import I from "./Icons";


interface BrandingPageProps {
    branding: any;
    onChange: (branding: any) => void;
    theme: any;
    reportName?: string;
}

// ─── Reusable primitives ───

const Label = ({ children, theme }: { children: string; theme: any }) => (
    <label style={{
        fontSize: 11, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: ".08em", color: theme.textSecondary, marginBottom: 4, display: "block",
    }}>{children}</label>
);

const TextInput = ({ label, value, onChange, placeholder, theme, mono }: {
    label: string; value: string; onChange: (v: string) => void;
    placeholder?: string; theme: any; mono?: boolean;
}) => (
    <div>
        <Label theme={theme}>{label}</Label>
        <input type="text" value={value} onChange={e => onChange(e.target.value)}
               placeholder={placeholder}
               style={{
                   width: "100%", padding: "8px 10px", background: theme.background,
                   border: `1px solid ${theme.border}`, borderRadius: 6,
                   color: theme.text, fontSize: 13, outline: "none",
                   ...(mono ? { fontFamily: "monospace", fontSize: 12 } : {}),
               }} />
    </div>
);

const NumberInput = ({ label, value, onChange, min, max, suffix, theme }: {
    label: string; value: number; onChange: (v: number) => void;
    min?: number; max?: number; suffix?: string; theme: any;
}) => (
    <div>
        <Label theme={theme}>{label}</Label>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <input type="number" value={value} min={min} max={max}
                   onChange={e => onChange(parseInt(e.target.value) || 0)}
                   style={{
                       width: "100%", padding: "8px 10px", background: theme.background,
                       border: `1px solid ${theme.border}`, borderRadius: 6,
                       color: theme.text, fontSize: 13, outline: "none",
                   }} />
            {suffix && <span style={{ fontSize: 11, color: theme.textSecondary, whiteSpace: "nowrap" }}>{suffix}</span>}
        </div>
    </div>
);

const ColorPicker = ({ label, value, onChange, theme }: {
    label: string; value: string; onChange: (v: string) => void; theme: any;
}) => (
    <div>
        <Label theme={theme}>{label}</Label>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input type="color" value={value} onChange={e => onChange(e.target.value)}
                   style={{ width: 40, height: 32, border: `1px solid ${theme.border}`, borderRadius: 6, cursor: "pointer" }} />
            <input type="text" value={value} onChange={e => onChange(e.target.value)}
                   style={{
                       flex: 1, padding: "6px 8px", background: theme.background,
                       border: `1px solid ${theme.border}`, borderRadius: 6,
                       color: theme.text, fontSize: 12, outline: "none",
                   }} />
        </div>
    </div>
);

const SelectInput = ({ label, value, onChange, options, theme }: {
    label: string; value: string; onChange: (v: string) => void;
    options: { v: string; l: string }[]; theme: any;
}) => (
    <div>
        <Label theme={theme}>{label}</Label>
        <select value={value} onChange={e => onChange(e.target.value)}
                style={{
                    width: "100%", padding: "8px 10px", background: theme.background,
                    border: `1px solid ${theme.border}`, borderRadius: 6,
                    color: theme.text, fontSize: 13, outline: "none", cursor: "pointer",
                }}>
            {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
    </div>
);

const Card = ({ title, subtitle, children, theme, defaultOpen = true }: {
    title: string; subtitle?: string; children: React.ReactNode; theme: any; defaultOpen?: boolean;
}) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div style={{
            background: theme.cardBackground, borderRadius: 10,
            border: `1px solid ${theme.border}`, overflow: "hidden",
        }}>
            <div onClick={() => setOpen(!open)} style={{
                padding: "12px 16px", cursor: "pointer", userSelect: "none",
                display: "flex", alignItems: "center", gap: 8,
            }}>
        <span style={{ color: theme.textSecondary, fontSize: 12 }}>
          {open ? <I.Down /> : <I.Right />}
        </span>
                <div style={{ flex: 1 }}>
                    <h4 style={{ fontSize: 15, fontWeight: 700, color: theme.text, margin: 0 }}>{title}</h4>
                    {subtitle && <p style={{ fontSize: 12, color: theme.textSecondary, margin: "2px 0 0" }}>{subtitle}</p>}
                </div>
            </div>
            {open && <div style={{ padding: "0 16px 16px" }}>{children}</div>}
        </div>
    );
};

// ─── Image upload hook ───

function useImageUpload(currentBase64: string, currentUrl: string, onSet: (b64: string, url: string) => void) {
    const [dragOver, setDragOver] = useState(false);

    const handleFile = (file: File) => {
        if (!file.type.startsWith("image/")) return;
        const reader = new FileReader();
        reader.onload = () => onSet(reader.result as string, "");
        reader.readAsDataURL(file);
    };

    const onDrop = (e: React.DragEvent) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]); };
    const onFileInput = (e: React.ChangeEvent<HTMLInputElement>) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); };
    const clear = () => onSet("", "");
    const hasSrc = !!(currentBase64 || currentUrl);
    const src = currentBase64 || currentUrl;

    return { dragOver, setDragOver, onDrop, onFileInput, clear, hasSrc, src };
}

const ImageDropZone = ({ label, base64Key, urlKey, branding, onChange, theme, hint }: {
    label: string; base64Key: string; urlKey: string;
    branding: any; onChange: (b: any) => void; theme: any; hint?: string;
}) => {
    const img = useImageUpload(
        branding[base64Key] || "", branding[urlKey] || "",
        (b64, url) => onChange({ ...branding, [base64Key]: b64, [urlKey]: url }),
    );

    return (
        <div>
            <Label theme={theme}>{label}</Label>
            <div
                onDragOver={e => { e.preventDefault(); img.setDragOver(true); }}
                onDragLeave={() => img.setDragOver(false)}
                onDrop={img.onDrop}
                style={{
                    border: `2px dashed ${img.dragOver ? theme.blue : theme.border}`,
                    borderRadius: 8, padding: "12px", textAlign: "center",
                    background: img.dragOver ? `${theme.blue}08` : theme.background,
                    transition: "all 0.15s",
                }}>
                {img.hasSrc ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
                        <img src={img.src} alt={label}
                             style={{ maxHeight: 48, maxWidth: 200, objectFit: "contain", borderRadius: 4 }} />
                        <button onClick={img.clear} style={{
                            background: "none", border: `1px solid ${theme.border}`, borderRadius: 6,
                            padding: "4px 10px", fontSize: 12, color: theme.textSecondary, cursor: "pointer",
                        }}>Remove</button>
                    </div>
                ) : (
                    <label style={{ cursor: "pointer", display: "block" }}>
                        <div style={{ fontSize: 13, color: theme.textSecondary, marginBottom: 2 }}>
                            Drop image or <span style={{ color: theme.blue, fontWeight: 600 }}>browse</span>
                        </div>
                        {hint && <div style={{ fontSize: 11, color: theme.textSecondary, opacity: 0.6 }}>{hint}</div>}
                        <input type="file" accept="image/*" onChange={img.onFileInput} style={{ display: "none" }} />
                    </label>
                )}
            </div>
        </div>
    );
};

// ─── Font presets ───

const FONT_PRESETS = [
    { v: "DM Sans, sans-serif", l: "DM Sans", url: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" },
    { v: "Inter, sans-serif", l: "Inter", url: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" },
    { v: "Roboto, sans-serif", l: "Roboto", url: "https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap" },
    { v: "Poppins, sans-serif", l: "Poppins", url: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap" },
    { v: "Lato, sans-serif", l: "Lato", url: "https://fonts.googleapis.com/css2?family=Lato:wght@400;700;900&display=swap" },
    { v: "Open Sans, sans-serif", l: "Open Sans", url: "https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700;800&display=swap" },
    { v: "Montserrat, sans-serif", l: "Montserrat", url: "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap" },
    { v: "Source Sans Pro, sans-serif", l: "Source Sans Pro", url: "https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@400;600;700&display=swap" },
    { v: "Nunito, sans-serif", l: "Nunito", url: "https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800&display=swap" },
    { v: "custom", l: "Custom…" },
];

const DEFAULT_FONT_SIZES = {
    cover_title: 45,
    cover_subtitle: 19,
    cover_meta: 15,
    section_label: 13,
    page_title: 27,
    chart_title: 15,
    axis_label: 12,
    legend: 12,
    body: 14,
    footer: 14,
    trend_annotation: 12,
};

// ─── Placement utilities ───

const PLACEMENTS = [
    ["top-left", "top-center", "top-right"],
    ["left", "center", "right"],
    ["bottom-left", "bottom-center", "bottom-right"],
];

function parsePlacement(p: string): [string, string] {
    if (p.includes("-")) {
        const parts = p.split("-");
        if (parts[0] === "top" || parts[0] === "bottom") return [parts[0], parts[1]];
    }
    return ["center", p || "center"];
}

function getPlacementStyles(placement: string) {
    const [v, h] = parsePlacement(placement);
    return {
        justifyContent: v === "top" ? "flex-start" : v === "bottom" ? "flex-end" : "center",
        alignItems: h === "left" ? "flex-start" : h === "right" ? "flex-end" : "center",
        textAlign: (h === "left" || h === "right" ? h : "center") as "left" | "center" | "right",
    };
}

const PlacementGrid = ({ value, onChange, theme, label }: {
    value: string; onChange: (v: string) => void; theme: any; label: string;
}) => (
    <div>
        <Label theme={theme}>{label}</Label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 28px)", gap: 3 }}>
            {PLACEMENTS.flat().map(pos => (
                <div key={pos} onClick={() => onChange(pos)}
                     title={pos.replace("-", " ")}
                     style={{
                         width: 28, height: 20, borderRadius: 4, cursor: "pointer",
                         border: `1.5px solid ${value === pos ? theme.blue : theme.border}`,
                         background: value === pos ? theme.blue : "transparent",
                         display: "flex", alignItems: "center", justifyContent: "center",
                         transition: "all 0.1s",
                     }}>
                    <div style={{
                        width: 4, height: 4, borderRadius: 2,
                        background: value === pos ? "#fff" : theme.textSecondary,
                        opacity: value === pos ? 1 : 0.3,
                    }} />
                </div>
            ))}
        </div>
    </div>
);

/** Convert double-spaces to line breaks for multi-line titles */
const Multiline = ({ text }: { text: string }) => {
    if (!text || !text.includes("  ")) return <>{text}</>;
    const parts = text.split("  ");
    return <>{parts.map((p, i) => <span key={i}>{i > 0 && <br />}{p}</span>)}</>;
};

// ─── Dummy SVG Charts ───

const DummyBarChart = ({ palette, branding, title }: { palette: string[]; branding: any; title: string }) => {
    const data = [[65, 45, 80, 55, 70], [40, 60, 50, 75, 45]];
    const barW = 14, gap = 10, chartH = 100, chartW = 200, max = 85;
    const labels = ["Jan", "Feb", "Mar", "Apr", "May"];
    return (
        <div style={{ background: branding.card_bg || "#fff", border: `1px solid ${branding.card_border || "#e5e7eb"}`, borderRadius: 8, overflow: "hidden", display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ padding: "6px 10px", borderBottom: `1px solid ${branding.card_border || "#e5e7eb"}` }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: branding.text_color || "#1a1a1a", fontFamily: branding.font_family }}>{title}</span>
            </div>
            <div style={{ padding: "8px 6px", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg viewBox={`0 0 ${chartW} ${chartH + 16}`} width="100%" preserveAspectRatio="xMidYMid meet">
                    <line x1="10" y1={chartH} x2={chartW - 5} y2={chartH} stroke={branding.muted_color || "#6b7280"} strokeWidth="0.5" opacity="0.3" />
                    {[0.25, 0.5, 0.75].map(f => <line key={f} x1="10" y1={chartH * (1 - f)} x2={chartW - 5} y2={chartH * (1 - f)} stroke={branding.muted_color || "#6b7280"} strokeWidth="0.3" opacity="0.15" />)}
                    {data[0].map((_, gi) => {
                        const gx = gi * (barW * 2 + gap) + 18;
                        return data.map((s, si) => (
                            <rect key={`${gi}-${si}`} x={gx + si * barW} y={chartH - (s[gi] / max) * chartH}
                                  width={barW - 2} height={(s[gi] / max) * chartH}
                                  fill={palette[si % palette.length] || "#6366f1"} rx="2" />
                        ));
                    })}
                    {labels.map((l, i) => (
                        <text key={i} x={i * (barW * 2 + gap) + 18 + barW} y={chartH + 11}
                              textAnchor="middle" fontSize="7" fill={branding.muted_color || "#6b7280"}>{l}</text>
                    ))}
                </svg>
            </div>
        </div>
    );
};

const DummyLineChart = ({ palette, branding, title }: { palette: string[]; branding: any; title: string }) => {
    const series = [[20, 35, 28, 55, 45, 60, 70], [40, 25, 50, 35, 55, 40, 55]];
    const chartW = 200, chartH = 100, max = 80;
    const pts = (vals: number[]) => vals.map((v, i) => `${10 + (i / (vals.length - 1)) * (chartW - 20)},${chartH - (v / max) * chartH}`).join(" ");
    const labels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"];
    return (
        <div style={{ background: branding.card_bg || "#fff", border: `1px solid ${branding.card_border || "#e5e7eb"}`, borderRadius: 8, overflow: "hidden", display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ padding: "6px 10px", borderBottom: `1px solid ${branding.card_border || "#e5e7eb"}` }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: branding.text_color || "#1a1a1a", fontFamily: branding.font_family }}>{title}</span>
            </div>
            <div style={{ padding: "8px 6px", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg viewBox={`0 0 ${chartW} ${chartH + 16}`} width="100%" preserveAspectRatio="xMidYMid meet">
                    {[0.25, 0.5, 0.75].map(f => <line key={f} x1="10" y1={chartH * (1 - f)} x2={chartW - 5} y2={chartH * (1 - f)} stroke={branding.muted_color || "#6b7280"} strokeWidth="0.3" opacity="0.15" />)}
                    <line x1="10" y1={chartH} x2={chartW - 5} y2={chartH} stroke={branding.muted_color || "#6b7280"} strokeWidth="0.5" opacity="0.3" />
                    {series.map((s, i) => (
                        <polyline key={i} points={pts(s)} fill="none"
                                  stroke={palette[i % palette.length] || "#6366f1"} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                    ))}
                    {labels.map((l, i) => (
                        <text key={i} x={10 + (i / (labels.length - 1)) * (chartW - 20)} y={chartH + 11}
                              textAnchor="middle" fontSize="7" fill={branding.muted_color || "#6b7280"}>{l}</text>
                    ))}
                </svg>
            </div>
        </div>
    );
};

// ─── Preview Page Components ───

const CoverPagePreview = ({ branding: b, reportName }: { branding: any; reportName?: string }) => {
    const fs = b.font_sizes || DEFAULT_FONT_SIZES;
    const gradStart = b.cover_gradient_start || b.primary_color || "#e91e8c";
    const bgColor = b.bg_color || "#ffffff";
    const placement = getPlacementStyles(b.cover_text_placement || "center");
    const logoSrc = b.logo_base64 || b.logo_url;
    const coverImgSrc = b.cover_image_base64 || b.cover_image_url;
    const fitMap: Record<string, { size: string; pos: string }> = {
        contain: { size: "contain", pos: "center" },
        "top-banner": { size: "100% auto", pos: "top" },
        "bottom-banner": { size: "100% auto", pos: "bottom" },
    };
    const imgFit = fitMap[b.cover_image_fit] || { size: "cover", pos: "center" };

    return (
        <div style={{
            width: "100%", aspectRatio: "420/297", position: "relative", overflow: "hidden",
            background: `linear-gradient(135deg, ${bgColor} 0%, ${gradStart}15 50%, ${bgColor} 100%)`,
            fontFamily: b.font_family || "DM Sans, sans-serif",
        }}>
            {coverImgSrc && (
                <div style={{
                    position: "absolute", inset: 0,
                    backgroundImage: `url(${coverImgSrc})`,
                    backgroundSize: imgFit.size, backgroundPosition: imgFit.pos,
                    backgroundRepeat: "no-repeat",
                    opacity: (b.cover_image_opacity ?? 100) / 100,
                }} />
            )}
            <div style={{
                position: "relative", zIndex: 1, width: "100%", height: "100%",
                display: "flex", flexDirection: "column",
                justifyContent: placement.justifyContent,
                alignItems: placement.alignItems,
                textAlign: placement.textAlign,
                padding: "8%", overflow: "hidden",
            }}>
                {logoSrc && b.cover_logo_enabled !== false && (b.cover_logo_placement === "top" || b.cover_logo_placement === "left") && (
                    <img src={logoSrc} alt="" style={{ maxHeight: "5%", maxWidth: "25%", objectFit: "contain", marginBottom: 6, flexShrink: 0 }} />
                )}
                <div style={{ fontSize: `min(3.5cqi, ${fs.cover_title * 0.4}px)`, fontWeight: 800, color: b.cover_title_color || b.primary_color, letterSpacing: "-0.02em", marginBottom: 3, lineHeight: 1.15, wordBreak: "break-word", maxWidth: "100%" }}>
                    <Multiline text={reportName || "Report Title"} />
                </div>
                <div style={{ fontSize: `min(1.8cqi, ${fs.cover_subtitle * 0.5}px)`, color: b.cover_subtitle_color || b.text_color || "#1a1a1a", lineHeight: 1.3 }}>
                    January 2026
                </div>
                {logoSrc && b.cover_logo_enabled !== false && (b.cover_logo_placement === "bottom" || b.cover_logo_placement === "right") && (
                    <img src={logoSrc} alt="" style={{ maxHeight: "5%", maxWidth: "25%", objectFit: "contain", marginTop: 8, flexShrink: 0 }} />
                )}
            </div>
        </div>
    );
};

const SectionDividerPreview = ({ branding: b }: { branding: any }) => {
    const fs = b.font_sizes || DEFAULT_FONT_SIZES;
    const [v, h] = parsePlacement(b.section_title_placement || "center");
    const sectionImgSrc = b.section_image_base64 || b.section_image_url;
    const fitMap: Record<string, { size: string; pos: string }> = {
        contain: { size: "contain", pos: "center" },
        "top-banner": { size: "100% auto", pos: "top" },
        "bottom-banner": { size: "100% auto", pos: "bottom" },
    };
    const imgFit = fitMap[b.section_image_fit] || { size: "cover", pos: "center" };

    const vPos: Record<string, any> = {
        top: { top: "8%", bottom: "auto" },
        bottom: { bottom: "8%", top: "auto" },
        center: { top: "50%", transform: "translateY(-50%)" },
    };
    const posStyle = vPos[v] || vPos.center;

    return (
        <div style={{
            width: "100%", aspectRatio: "420/297", position: "relative", overflow: "hidden",
            background: b.bg_color || "#ffffff",
            fontFamily: b.font_family || "DM Sans, sans-serif",
        }}>
            {sectionImgSrc && (
                <div style={{
                    position: "absolute", inset: 0,
                    backgroundImage: `url(${sectionImgSrc})`,
                    backgroundSize: imgFit.size, backgroundPosition: imgFit.pos,
                    backgroundRepeat: "no-repeat",
                    opacity: (b.section_image_opacity ?? 100) / 100,
                }} />
            )}
            <div style={{
                position: "absolute", left: "8%", right: "8%",
                textAlign: h as any,
                ...posStyle,
            }}>
                <div style={{
                    fontSize: `min(3.2cqi, ${fs.cover_title * 0.38}px)`, fontWeight: 800,
                    color: b.section_title_color || b.primary_color,
                    textShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: 3,
                    lineHeight: 1.15, wordBreak: "break-word",
                }}>
                    Section Title
                </div>
                <div style={{
                    fontSize: `min(1.5cqi, ${fs.cover_subtitle * 0.45}px)`,
                    color: b.section_subtitle_color || b.muted_color || "#6b7280",
                    lineHeight: 1.3,
                }}>
                    Section description goes here
                </div>
            </div>
        </div>
    );
};

const ContentPagePreview = ({ branding: b }: { branding: any }) => {
    const fs = b.font_sizes || DEFAULT_FONT_SIZES;
    const palette = b.chart_palette || ["#e91e8c", "#4a0e5c", "#f59e0b"];
    const logoSrc = b.logo_base64 || b.logo_url;

    return (
        <div style={{
            width: "100%", aspectRatio: "420/297", overflow: "hidden",
            background: b.bg_color || "#ffffff", padding: "4%", display: "flex", flexDirection: "column",
            fontFamily: b.font_family || "DM Sans, sans-serif",
        }}>
            {/* Header */}
            <div style={{
                paddingBottom: 3, borderBottom: `1px solid ${b.card_border || "#e5e7eb"}`,
                marginBottom: 6, flexShrink: 0,
            }}>
                <div style={{
                    fontSize: `min(1cqi, ${fs.section_label * 0.7}px)`, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: "0.1em",
                    color: b.primary_color || "#e91e8c", marginBottom: 1,
                }}>Operations</div>
                <div style={{
                    fontSize: `min(2cqi, ${fs.page_title * 0.45}px)`, fontWeight: 700,
                    color: b.text_color || "#1a1a1a",
                }}>
                    Monthly Overview
                    {b.header_style === "badge" && (
                        <span style={{
                            display: "inline-block", marginLeft: 4, padding: "1px 6px", borderRadius: 10,
                            background: `${b.primary_color || "#e91e8c"}18`,
                            border: `1px solid ${b.primary_color || "#e91e8c"}30`,
                            fontSize: `min(0.7cqi, 8px)`, color: b.primary_color || "#e91e8c",
                            fontWeight: 600, verticalAlign: "middle",
                        }}>Entity</span>
                    )}
                </div>
            </div>

            {/* Charts grid - 3 columns */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, flex: 1, minHeight: 0 }}>
                <DummyBarChart palette={palette} branding={b} title="Revenue by Region" />
                <DummyBarChart palette={palette} branding={b} title="Units Sold" />
                <DummyLineChart palette={palette} branding={b} title="Trend Analysis" />
            </div>

            {/* Legend */}
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 4 }}>
                {palette.slice(0, 2).map((c: string, i: number) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <div style={{ width: 5, height: 5, borderRadius: 3, background: c }} />
                        <span style={{ fontSize: `min(0.8cqi, 7px)`, color: b.muted_color || "#6b7280" }}>Series {i + 1}</span>
                    </div>
                ))}
            </div>

            {/* Footer */}
            {b.footer_position !== "none" && (
                <div style={{
                    marginTop: 4, paddingTop: 3, borderTop: `1px solid ${b.card_border || "#e5e7eb"}`,
                    display: "flex", flexShrink: 0,
                    justifyContent: b.footer_position === "bottom-center" ? "center"
                        : b.footer_position === "bottom-right" ? "flex-end" : "flex-start",
                }}>
                    {logoSrc ? (
                        <img src={logoSrc} alt="" style={{ maxHeight: 10, opacity: 0.5, objectFit: "contain" }} />
                    ) : (
                        <span style={{
                            fontSize: `min(0.7cqi, 7px)`, fontWeight: 700, letterSpacing: "0.15em",
                            textTransform: "uppercase", color: b.muted_color || "#6b7280", opacity: 0.5,
                        }}>{b.company_name || "Company"}</span>
                    )}
                </div>
            )}
        </div>
    );
};

// ─── Preview Thumbnail Wrapper ───

const PreviewThumbnail = ({ label, theme, onExpand, children }: {
    label: string; theme: any; onExpand: () => void; children: React.ReactNode;
}) => (
    <div style={{
        borderRadius: 8, overflow: "hidden",
        border: `1px solid ${theme.border}`, position: "relative",
    }}>
        <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "4px 8px", background: theme.cardBackground, borderBottom: `1px solid ${theme.border}`,
        }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: theme.textSecondary }}>{label}</span>
            <button onClick={onExpand} title="View fullscreen"
                    style={{
                        background: "none", border: "none", cursor: "pointer",
                        fontSize: 12, color: theme.textSecondary, padding: "2px 4px",
                        borderRadius: 4, lineHeight: 1,
                    }}>⛶</button>
        </div>
        <div style={{ containerType: "inline-size" }}>
            {children}
        </div>
    </div>
);

// ─── Entity Color Manager ───

interface ColumnSource {
    table: string;
    column: string;
    tableName: string;
}

// Individual entity color item with inline editing
function EntityColorItem({ entityName, color, sources, theme, onColorChange, onNameChange, onHexChange, onRemove }: {
    entityName: string;
    color?: string;
    sources: string[];
    theme: any;
    onColorChange: (color: string) => void;
    onNameChange: (newName: string) => void;
    onHexChange: (hex: string) => void;
    onRemove: () => void;
}) {
    const [editingName, setEditingName] = useState(false);
    const [editingHex, setEditingHex] = useState(false);
    const [tempName, setTempName] = useState(entityName);
    const [tempHex, setTempHex] = useState(color || "#6366f1");
    const nameInputRef = useRef<HTMLInputElement>(null);
    const hexInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (editingName && nameInputRef.current) {
            nameInputRef.current.focus();
            nameInputRef.current.select();
        }
    }, [editingName]);

    useEffect(() => {
        if (editingHex && hexInputRef.current) {
            hexInputRef.current.focus();
            hexInputRef.current.select();
        }
    }, [editingHex]);

    const handleNameSave = () => {
        if (tempName.trim() && tempName !== entityName) {
            onNameChange(tempName.trim());
        }
        setEditingName(false);
    };

    const handleHexSave = () => {
        if (tempHex.trim()) {
            onHexChange(tempHex.trim());
        }
        setEditingHex(false);
    };

    const handleNameKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleNameSave();
        if (e.key === 'Escape') { setTempName(entityName); setEditingName(false); }
    };

    const handleHexKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleHexSave();
        if (e.key === 'Escape') { setTempHex(color || "#6366f1"); setEditingHex(false); }
    };

    return (
        <div style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "6px 8px", borderRadius: 6,
            background: color ? `${color}10` : theme.background,
            border: `1px solid ${color ? color + "40" : theme.border}`,
        }}>
            <input type="color" value={color || "#6366f1"}
                   onChange={e => onColorChange(e.target.value)}
                   style={{ width: 28, height: 28, border: "none", cursor: "pointer", borderRadius: 4 }} />
            
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Entity name - editable */}
                {editingName ? (
                    <input
                        ref={nameInputRef}
                        type="text"
                        value={tempName}
                        onChange={e => setTempName(e.target.value)}
                        onBlur={handleNameSave}
                        onKeyDown={handleNameKeyDown}
                        style={{
                            fontSize: 12, color: theme.text, background: theme.background,
                            border: `1px solid ${theme.blue}`, borderRadius: 3, padding: "2px 4px",
                            outline: "none", width: "100%",
                        }}
                    />
                ) : (
                    <div
                        onClick={() => { setTempName(entityName); setEditingName(true); }}
                        style={{
                            fontSize: 12, color: theme.text, cursor: "text",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            padding: "2px 4px", borderRadius: 3,
                        }}
                        title="Click to edit"
                    >
                        {entityName}
                    </div>
                )}
                
                {/* Hex code - editable */}
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    {editingHex ? (
                        <input
                            ref={hexInputRef}
                            type="text"
                            value={tempHex}
                            onChange={e => setTempHex(e.target.value)}
                            onBlur={handleHexSave}
                            onKeyDown={handleHexKeyDown}
                            placeholder="#000000"
                            style={{
                                fontSize: 10, color: theme.textSecondary, background: theme.background,
                                border: `1px solid ${theme.blue}`, borderRadius: 3, padding: "1px 4px",
                                outline: "none", fontFamily: "monospace", width: 80,
                            }}
                        />
                    ) : (
                        <div
                            onClick={() => { setTempHex(color || "#6366f1"); setEditingHex(true); }}
                            style={{
                                fontSize: 10, color: theme.textSecondary, cursor: "text",
                                fontFamily: "monospace", padding: "1px 4px", borderRadius: 3,
                            }}
                            title="Click to edit hex code"
                        >
                            {color || "#6366f1"}
                        </div>
                    )}
                    {sources.length > 1 && (
                        <div style={{ fontSize: 9, color: theme.textSecondary, opacity: 0.6 }}>
                            · {sources.length} cols
                        </div>
                    )}
                </div>
            </div>
            
            {color && (
                <button onClick={onRemove} style={{
                    background: "none", border: "none", color: theme.textSecondary,
                    cursor: "pointer", padding: "2px 4px", fontSize: 16, lineHeight: 1,
                    opacity: 0.6,
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                onMouseLeave={e => e.currentTarget.style.opacity = "0.6"}
                title="Remove color mapping"
                >×</button>
            )}
        </div>
    );
}

function EntityColorSection({ branding, onChange, theme }: { branding: any; onChange: (b: any) => void; theme: any }) {
    const TABLES = useTables();
    // Flat map: value → color (shared across all columns)
    const entityColors: Record<string, string> = branding.entity_colors || {};

    const [loadedValues, setLoadedValues] = useState<Map<string, Set<string>>>(new Map());
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("");
    const [selectedSources, setSelectedSources] = useState<ColumnSource[]>([]);
    const [forceUpdate, setForceUpdate] = useState(0);

    // Ensure component updates when entity colors are loaded/changed
    useEffect(() => {
        // Force a re-render when entityColors has items but no columns are loaded
        if (Object.keys(entityColors).length > 0 && loadedValues.size === 0) {
            setForceUpdate(prev => prev + 1);
        }
    }, [entityColors, loadedValues.size]);

    // All available text columns across all tables
    const allColumns: ColumnSource[] = TABLES.flatMap(t =>
        (t.text_fields || []).map((f: string) => ({
            table: t.id,
            column: f,
            tableName: t.name,
        }))
    );

    const toggleSource = (src: ColumnSource) => {
        setSelectedSources(prev => {
            const exists = prev.some(s => s.table === src.table && s.column === src.column);
            return exists
                ? prev.filter(s => !(s.table === src.table && s.column === src.column))
                : [...prev, src];
        });
    };

    const isSelected = (src: ColumnSource) =>
        selectedSources.some(s => s.table === src.table && s.column === src.column);

    const loadSelectedValues = async () => {
        if (selectedSources.length === 0) return;
        setLoading(true);
        const next = new Map(loadedValues);
        try {
            const { ReportBuilderAPI } = await import("../../../../utils/ReportBuilderAPI");
            
            const requests = selectedSources
                .filter(src => !next.has(`${src.table}/${src.column}`))
                .map(src => ({
                    table: src.table,
                    column: src.column,
                    exclude_others: true,
                }));
            
            const results = await ReportBuilderAPI.prefetchMultiple(requests);
            
            results.forEach((data, key) => {
                next.set(key, new Set(data.values || []));
            });
            
            setLoadedValues(next);
        } finally {
            setLoading(false);
        }
    };

    const loadAllColumns = async () => {
        setLoading(true);
        setSelectedSources(allColumns);
        const next = new Map<string, Set<string>>();
        try {
            const { ReportBuilderAPI } = await import("../../../../utils/ReportBuilderAPI");
            
            const requests = allColumns.map(src => ({
                table: src.table,
                column: src.column,
                exclude_others: true,
            }));
            
            const results = await ReportBuilderAPI.prefetchMultiple(requests);
            
            results.forEach((data, key) => {
                next.set(key, new Set(data.values || []));
            });
            
            setLoadedValues(next);
        } finally {
            setLoading(false);
        }
    };

    // Deduplicated sorted list of all loaded values + uploaded entities
    const allValues = (() => {
        const set = new Set<string>();
        loadedValues.forEach(vals => vals.forEach(v => set.add(v)));
        // Also include any entities that have been uploaded/colored
        Object.keys(entityColors).forEach(entity => set.add(entity));
        const arr = Array.from(set).sort((a, b) => a.localeCompare(b));
        if (!filter) return arr;
        const f = filter.toLowerCase();
        return arr.filter(v => v.toLowerCase().includes(f));
    })();

    // Which columns a value appears in (for the tag display)
    const valueSourcesMap = (val: string): string[] => {
        const sources: string[] = [];
        loadedValues.forEach((vals, key) => {
            if (vals.has(val)) {
                const col = key.split("/")[1] || key;
                if (!sources.includes(col)) sources.push(col);
            }
        });
        return sources;
    };

    const coloredCount = allValues.filter(v => entityColors[v]).length;

    const setColor = (value: string, color: string) => {
        onChange({ ...branding, entity_colors: { ...entityColors, [value]: color } });
    };

    const removeColor = (value: string) => {
        const next = { ...entityColors };
        delete next[value];
        onChange({ ...branding, entity_colors: next });
    };

    const renameEntity = (oldName: string, newName: string) => {
        if (!newName || newName === oldName) return;
        const next = { ...entityColors };
        if (next[newName]) {
            alert(`Entity "${newName}" already exists`);
            return;
        }
        next[newName] = next[oldName];
        delete next[oldName];
        onChange({ ...branding, entity_colors: next });
    };

    const updateHexCode = (entity: string, newHex: string) => {
        // Validate and normalize hex code
        let hex = newHex.trim();
        if (!hex.startsWith('#')) hex = '#' + hex;
        if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
            setColor(entity, hex.toLowerCase());
        }
    };

    const exportToCSV = () => {
        if (Object.keys(entityColors).length === 0) {
            alert('No entity colors to export');
            return;
        }

        // Generate CSV content
        let csv = 'entity,hex_code\n';
        Object.entries(entityColors)
            .sort(([a], [b]) => a.localeCompare(b))
            .forEach(([entity, color]) => {
                // Escape entity name if it contains comma, semicolon, or quotes
                const needsQuotes = entity.includes(',') || entity.includes(';') || entity.includes('"');
                const escapedEntity = needsQuotes ? `"${entity.replace(/"/g, '""')}"` : entity;
                csv += `${escapedEntity},${color}\n`;
            });

        // Create blob and download
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `entity_colors_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const autoAssignAll = () => {
        const palette = branding.chart_palette || ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];
        const next = { ...entityColors };
        allValues.forEach((v, i) => {
            if (!next[v]) next[v] = palette[i % palette.length];
        });
        onChange({ ...branding, entity_colors: next });
    };

    const autoAssignUncolored = () => {
        const palette = branding.chart_palette || ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];
        const next = { ...entityColors };
        let idx = Object.keys(next).length;
        allValues.forEach(v => {
            if (!next[v]) { next[v] = palette[idx % palette.length]; idx++; }
        });
        onChange({ ...branding, entity_colors: next });
    };

    const clearAll = () => onChange({ ...branding, entity_colors: {} });

    // Detect separator (comma, semicolon, or tab) by analyzing the first few lines
    const detectSeparator = (text: string): string => {
        const lines = text.split('\n').slice(0, 5).filter(l => l.trim()); // Check first 5 lines
        if (lines.length === 0) return ',';

        const separators = [',', ';', '\t'];
        const counts: Record<string, number> = { ',': 0, ';': 0, '\t': 0 };

        // Count occurrences of each separator across lines
        lines.forEach(line => {
            separators.forEach(sep => {
                const parts = line.split(sep);
                if (parts.length >= 2) {
                    counts[sep] += parts.length - 1;
                }
            });
        });

        // Return separator with highest count
        let maxCount = 0;
        let detectedSep = ',';
        separators.forEach(sep => {
            if (counts[sep] > maxCount) {
                maxCount = counts[sep];
                detectedSep = sep;
            }
        });

        return detectedSep;
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const text = event.target?.result as string;
                const next = { ...entityColors };
                let imported = 0;

                // Parse CSV or Excel-like format
                const lines = text.split('\n').map(l => l.trim()).filter(l => l);
                
                // Detect separator (comma, semicolon, or tab)
                const separator = detectSeparator(text);
                
                // Skip header row if it looks like headers
                let startIdx = 0;
                if (lines.length > 0) {
                    const firstLine = lines[0].toLowerCase();
                    if (firstLine.includes('entity') || firstLine.includes('name') || firstLine.includes('color') || firstLine.includes('hex')) {
                        startIdx = 1;
                    }
                }

                for (let i = startIdx; i < lines.length; i++) {
                    const line = lines[i];
                    // Split by detected separator
                    const parts = line.split(separator);
                    
                    if (parts.length >= 2) {
                        const entity = parts[0].trim().replace(/^["']|["']$/g, ''); // Remove quotes
                        let hexCode = parts[1].trim().replace(/^["']|["']$/g, '');
                        
                        // Validate and normalize hex code
                        if (hexCode && entity) {
                            // Add # if missing
                            if (!hexCode.startsWith('#')) {
                                hexCode = '#' + hexCode;
                            }
                            // Validate hex format
                            if (/^#[0-9A-Fa-f]{6}$/.test(hexCode)) {
                                next[entity] = hexCode.toLowerCase();
                                imported++;
                            }
                        }
                    }
                }

                onChange({ ...branding, entity_colors: next });
                alert(`Successfully imported ${imported} color mapping${imported !== 1 ? 's' : ''}`);
            } catch (error: any) {
                alert(`Failed to parse file: ${error.message}`);
            }
        };
        
        reader.readAsText(file);
        // Reset input so same file can be uploaded again
        e.target.value = '';
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

            {/* Summary */}
            {Object.keys(entityColors).length > 0 && (
                <div style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
                    background: `${theme.blue}08`, borderRadius: 8, border: `1px solid ${theme.blue}20`,
                }}>
                    <div style={{ display: "flex", gap: 4, flex: 1, flexWrap: "wrap" }}>
                        {Object.entries(entityColors).slice(0, 12).map(([val, color]) => (
                            <div key={val} style={{
                                display: "flex", alignItems: "center", gap: 3, padding: "2px 8px",
                                borderRadius: 10, background: `${color}20`, fontSize: 11, fontWeight: 600, color,
                            }}>
                                <div style={{ width: 8, height: 8, borderRadius: 4, background: color }} />
                                {val}
                            </div>
                        ))}
                        {Object.keys(entityColors).length > 12 && (
                            <span style={{ fontSize: 11, color: theme.textSecondary, padding: "2px 4px" }}>
                +{Object.keys(entityColors).length - 12} more
              </span>
                        )}
                    </div>
                    <button onClick={clearAll} style={{
                        background: "none", border: `1px solid ${theme.border}`, borderRadius: 6,
                        padding: "3px 8px", fontSize: 11, color: theme.textSecondary, cursor: "pointer",
                        whiteSpace: "nowrap",
                    }}>Clear all</button>
                </div>
            )}

            {/* Column picker */}
            <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <Label theme={theme}>Select columns to load values from</Label>
                    <button onClick={loadAllColumns} disabled={loading} style={{
                        background: "none", border: `1px solid ${theme.border}`, borderRadius: 6,
                        padding: "3px 10px", fontSize: 11, color: theme.blue, cursor: loading ? "default" : "pointer",
                        fontWeight: 600, opacity: loading ? 0.5 : 1,
                    }}>Load all columns</button>
                </div>
                <div style={{
                    display: "flex", flexWrap: "wrap", gap: 4, padding: "8px",
                    background: theme.background, borderRadius: 8, border: `1px solid ${theme.border}`,
                    maxHeight: 120, overflowY: "auto",
                }}>
                    {allColumns.map(src => {
                        const sel = isSelected(src);
                        const loaded = loadedValues.has(`${src.table}/${src.column}`);
                        return (
                            <button key={`${src.table}/${src.column}`}
                                    onClick={() => toggleSource(src)}
                                    style={{
                                        padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 500,
                                        cursor: "pointer", border: `1px solid ${sel ? theme.blue : theme.border}`,
                                        background: sel ? `${theme.blue}15` : "transparent",
                                        color: sel ? theme.blue : theme.textSecondary,
                                        display: "flex", alignItems: "center", gap: 4,
                                    }}>
                                {loaded && <span style={{ width: 6, height: 6, borderRadius: 3, background: theme.success }} />}
                                {src.column.replace(/_/g, " ")}
                                <span style={{ opacity: 0.5, fontSize: 10 }}>({src.tableName})</span>
                            </button>
                        );
                    })}
                </div>
                {selectedSources.length > 0 && (
                    <button onClick={loadSelectedValues} disabled={loading} style={{
                        marginTop: 6, padding: "6px 14px", borderRadius: 6, fontSize: 12,
                        fontWeight: 600, cursor: loading ? "default" : "pointer",
                        border: "none", background: theme.buttonBackground, color: theme.buttonText,
                        opacity: loading ? 0.5 : 1, width: "100%",
                    }}>
                        {loading ? "Loading…" : `Load ${selectedSources.length} column${selectedSources.length > 1 ? "s" : ""}`}
                    </button>
                )}
            </div>

            {/* CSV/Excel Upload */}
            <div>
                <Label theme={theme}>Import from CSV/Excel</Label>
                <div style={{
                    padding: "12px", borderRadius: 8, background: theme.background,
                    border: `1px solid ${theme.border}`, display: "flex", flexDirection: "column", gap: 8,
                }}>
                    <div style={{ fontSize: 12, color: theme.textSecondary }}>
                        Upload a CSV or Excel file with two columns: <strong>entity</strong> and <strong>hex code</strong>. Additional columns will be ignored.
                    </div>
                    <div style={{ fontSize: 11, color: theme.textSecondary, opacity: 0.8, fontFamily: "monospace", background: theme.cardBackground, padding: "6px 8px", borderRadius: 4 }}>
                        Example: Product A,#e91e8c<br />
                        Product B,#4a0e5c
                    </div>
                    <label style={{
                        display: "inline-block", padding: "6px 14px", borderRadius: 6, fontSize: 12,
                        fontWeight: 600, cursor: "pointer", border: "none",
                        background: theme.buttonBackground, color: theme.buttonText,
                        textAlign: "center", width: "fit-content",
                    }}>
                        Choose File
                        <input type="file" accept=".csv,.tsv,.txt" onChange={handleFileUpload}
                               style={{ display: "none" }} />
                    </label>
                </div>
            </div>

            {/* Values list */}
            {allValues.length > 0 && (
                <div>
                    {/* Toolbar */}
                    <div style={{
                        display: "flex", gap: 6, alignItems: "center", marginBottom: 8, flexWrap: "wrap",
                    }}>
                        <input type="text" value={filter} onChange={e => setFilter(e.target.value)}
                               placeholder="Filter values…"
                               style={{
                                   flex: 1, minWidth: 140, padding: "6px 10px", background: theme.background,
                                   border: `1px solid ${theme.border}`, borderRadius: 6,
                                   color: theme.text, fontSize: 12, outline: "none",
                               }} />
                        <span style={{ fontSize: 11, color: theme.textSecondary, whiteSpace: "nowrap" }}>
              {allValues.length} values · {coloredCount} colored
            </span>
                        <button onClick={autoAssignAll}
                                style={{
                                    background: "none", border: `1px solid ${theme.border}`, borderRadius: 6,
                                    padding: "4px 10px", fontSize: 11, color: theme.textSecondary, cursor: "pointer",
                                    whiteSpace: "nowrap",
                                }}
                                onMouseEnter={e => e.currentTarget.style.color = theme.blue}
                                onMouseLeave={e => e.currentTarget.style.color = theme.textSecondary}
                        >Auto-assign all</button>
                        {coloredCount < allValues.length && coloredCount > 0 && (
                            <button onClick={autoAssignUncolored}
                                    style={{
                                        background: "none", border: `1px solid ${theme.border}`, borderRadius: 6,
                                        padding: "4px 10px", fontSize: 11, color: theme.textSecondary, cursor: "pointer",
                                        whiteSpace: "nowrap",
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.color = theme.blue}
                                    onMouseLeave={e => e.currentTarget.style.color = theme.textSecondary}
                            >Fill remaining</button>
                        )}
                        {coloredCount > 0 && (
                            <button onClick={exportToCSV}
                                    style={{
                                        background: theme.blue, border: "none", borderRadius: 6,
                                        padding: "4px 10px", fontSize: 11, color: "#ffffff", cursor: "pointer",
                                        whiteSpace: "nowrap", fontWeight: 600,
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.opacity = "0.9"}
                                    onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                            >Export CSV</button>
                        )}
                    </div>

                    {/* Grid of values */}
                    <div style={{
                        display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                        gap: 6, maxHeight: 360, overflowY: "auto", padding: "2px 0",
                    }}>
                        {allValues.map(val => {
                            const color = entityColors[val];
                            const sources = valueSourcesMap(val);
                            return (
                                <EntityColorItem
                                    key={val}
                                    entityName={val}
                                    color={color}
                                    sources={sources}
                                    theme={theme}
                                    onColorChange={(newColor) => setColor(val, newColor)}
                                    onNameChange={(newName) => renameEntity(val, newName)}
                                    onHexChange={(newHex) => updateHexCode(val, newHex)}
                                    onRemove={() => removeColor(val)}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
//  Main Component
// ═══════════════════════════════════════════════════════════════════

export default function BrandingPage({ branding, onChange, theme, reportName }: BrandingPageProps) {
    const b = branding;
    const set = (patch: any) => onChange({ ...b, ...patch });
    const fs = b.font_sizes || DEFAULT_FONT_SIZES;
    const setFs = (key: string, val: number) => set({ font_sizes: { ...fs, [key]: val } });

    const isCustomFont = !FONT_PRESETS.some(p => p.v === b.font_family && p.v !== "custom");

    const [previewExpanded, setPreviewExpanded] = useState(false);
    const [fullscreenPage, setFullscreenPage] = useState<"cover" | "divider" | "content" | null>(null);

    return (
        <div style={{ margin: "0 auto", padding: "20px 16px", maxWidth: 1400 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 3, color: theme.text }}>Report Branding</h3>
            <p style={{ fontSize: 14, color: theme.textSecondary, marginBottom: 20 }}>
                Customize the appearance of your generated reports
            </p>

            <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>

                {/* ═══ Left — Settings ═══ */}
                <div style={{ flex: 1, minWidth: 500, display: "flex", flexDirection: "column", gap: 12 }}>

                    {/* ── Company Info ── */}
                    <Card title="Company Information" theme={theme}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                            <TextInput label="Company Name" value={b.company_name}
                                       onChange={v => set({ company_name: v })} theme={theme} />
                            <TextInput label="Logo URL" value={b.logo_url} placeholder="https://…/logo.png"
                                       onChange={v => set({ logo_url: v, logo_base64: "" })} theme={theme} />
                        </div>
                        <ImageDropZone label="Logo Upload" base64Key="logo_base64" urlKey="logo_url"
                                       branding={b} onChange={onChange} theme={theme} hint="PNG, SVG, or JPG · Recommended height 48–80px" />
                    </Card>

                    {/* ── Cover & Section Images ── */}
                    <Card title="Page Images" subtitle="Background images for cover and section divider pages" theme={theme}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <ImageDropZone label="Cover Page Image" base64Key="cover_image_base64" urlKey="cover_image_url"
                                           branding={b} onChange={onChange} theme={theme} hint="1587×1122px recommended (A3 landscape)" />
                            <ImageDropZone label="Section Divider Image" base64Key="section_image_base64" urlKey="section_image_url"
                                           branding={b} onChange={onChange} theme={theme} hint="Used as background on section break pages" />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
                            <SelectInput label="Cover Image Fit" value={b.cover_image_fit || "cover"}
                                         onChange={v => set({ cover_image_fit: v })}
                                         options={[
                                             { v: "cover", l: "Cover (fill)" },
                                             { v: "contain", l: "Contain (fit)" },
                                             { v: "top-banner", l: "Top Banner" },
                                             { v: "bottom-banner", l: "Bottom Banner" },
                                         ]} theme={theme} />
                            <NumberInput label="Cover Image Opacity" value={b.cover_image_opacity ?? 100}
                                         onChange={v => set({ cover_image_opacity: Math.min(100, Math.max(0, v)) })}
                                         min={0} max={100} suffix="%" theme={theme} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
                            <SelectInput label="Section Image Fit" value={b.section_image_fit || "cover"}
                                         onChange={v => set({ section_image_fit: v })}
                                         options={[
                                             { v: "cover", l: "Cover (fill)" },
                                             { v: "contain", l: "Contain (fit)" },
                                             { v: "top-banner", l: "Top Banner" },
                                             { v: "bottom-banner", l: "Bottom Banner" },
                                         ]} theme={theme} />
                            <NumberInput label="Section Image Opacity" value={b.section_image_opacity ?? 100}
                                         onChange={v => set({ section_image_opacity: Math.min(100, Math.max(0, v)) })}
                                         min={0} max={100} suffix="%" theme={theme} />
                        </div>
                    </Card>

                    {/* ── Cover Page Options ── */}
                    <Card title="Cover Page Options" subtitle="Customize text placement, colors, and logo display" theme={theme}>
                        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 12, marginBottom: 12, alignItems: "end" }}>
                            <PlacementGrid label="Text Placement" value={b.cover_text_placement || "center"}
                                           onChange={v => set({ cover_text_placement: v })} theme={theme} />
                            <SelectInput label="Logo Placement" value={b.cover_logo_placement || "top"}
                                         onChange={v => set({ cover_logo_placement: v })}
                                         options={[
                                             { v: "top", l: "Top" },
                                             { v: "bottom", l: "Bottom" },
                                             { v: "left", l: "Left" },
                                             { v: "right", l: "Right" },
                                         ]} theme={theme} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                            <ColorPicker label="Title Color" value={b.cover_title_color || b.primary_color}
                                         onChange={v => set({ cover_title_color: v })} theme={theme} />
                            <ColorPicker label="Subtitle Color" value={b.cover_subtitle_color || b.text_color}
                                         onChange={v => set({ cover_subtitle_color: v })} theme={theme} />
                            <ColorPicker label="Meta Color" value={b.cover_meta_color || b.muted_color}
                                         onChange={v => set({ cover_meta_color: v })} theme={theme} />
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input type="checkbox" id="cover-logo-enabled"
                                   checked={b.cover_logo_enabled !== false}
                                   onChange={e => set({ cover_logo_enabled: e.target.checked })}
                                   style={{ width: 16, height: 16, cursor: "pointer" }} />
                            <label htmlFor="cover-logo-enabled" style={{
                                fontSize: 13, color: theme.text, cursor: "pointer", userSelect: "none"
                            }}>Show logo on cover page</label>
                        </div>
                    </Card>

                    {/* ── Section Divider Options ── */}
                    <Card title="Section Divider Options" subtitle="Customize section title placement and colors" theme={theme}>
                        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 12, alignItems: "end" }}>
                            <PlacementGrid label="Title Placement" value={b.section_title_placement || "center"}
                                           onChange={v => set({ section_title_placement: v })} theme={theme} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
                            <ColorPicker label="Title Color" value={b.section_title_color || b.primary_color}
                                         onChange={v => set({ section_title_color: v })} theme={theme} />
                            <ColorPicker label="Subtitle Color" value={b.section_subtitle_color || b.muted_color}
                                         onChange={v => set({ section_subtitle_color: v })} theme={theme} />
                        </div>
                    </Card>

                    {/* ── Typography ── */}
                    <Card title="Typography" subtitle="Font family and sizing across the report" theme={theme}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                            <SelectInput label="Font Preset" value={isCustomFont ? "custom" : b.font_family}
                                         onChange={v => {
                                             if (v === "custom") return;
                                             const preset = FONT_PRESETS.find(p => p.v === v);
                                             set({ font_family: v, font_url: preset?.url || "" });
                                         }}
                                         options={FONT_PRESETS.map(p => ({ v: p.v, l: p.l }))}
                                         theme={theme} />
                            {isCustomFont && (
                                <TextInput label="Custom Font Family" value={b.font_family}
                                           onChange={v => set({ font_family: v })} placeholder="e.g. Helvetica, Arial"
                                           theme={theme} mono />
                            )}
                            {isCustomFont && (
                                <TextInput label="Font CSS URL" value={b.font_url || ""}
                                           onChange={v => set({ font_url: v })}
                                           placeholder="https://fonts.googleapis.com/css2?family=…" theme={theme} mono />
                            )}
                        </div>

                        {/* Font preview */}
                        <div style={{
                            padding: "12px 14px", borderRadius: 8, background: theme.background,
                            border: `1px solid ${theme.border}`, marginBottom: 16,
                            fontFamily: b.font_family,
                        }}>
                            <div style={{ fontSize: fs.cover_title * 0.5, fontWeight: 800, color: theme.text, marginBottom: 2 }}>
                                Cover Title — {fs.cover_title}px
                            </div>
                            <div style={{ fontSize: fs.page_title * 0.7, fontWeight: 700, color: theme.text, marginBottom: 2 }}>
                                Page Title — {fs.page_title}px
                            </div>
                            <div style={{ fontSize: fs.section_label, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: b.primary_color || theme.blue, marginBottom: 2 }}>
                                Section Label — {fs.section_label}px
                            </div>
                            <div style={{ fontSize: fs.body, color: theme.textSecondary }}>
                                Body text / Insights — {fs.body}px
                            </div>
                        </div>

                        <Label theme={theme}>Font Sizes</Label>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                            <NumberInput label="Cover Title" value={fs.cover_title} onChange={v => setFs("cover_title", v)} min={20} max={72} suffix="px" theme={theme} />
                            <NumberInput label="Cover Subtitle" value={fs.cover_subtitle} onChange={v => setFs("cover_subtitle", v)} min={10} max={36} suffix="px" theme={theme} />
                            <NumberInput label="Cover Meta" value={fs.cover_meta} onChange={v => setFs("cover_meta", v)} min={8} max={24} suffix="px" theme={theme} />
                            <NumberInput label="Section Label" value={fs.section_label} onChange={v => setFs("section_label", v)} min={8} max={20} suffix="px" theme={theme} />
                            <NumberInput label="Page Title" value={fs.page_title} onChange={v => setFs("page_title", v)} min={14} max={48} suffix="px" theme={theme} />
                            <NumberInput label="Chart Title" value={fs.chart_title} onChange={v => setFs("chart_title", v)} min={10} max={24} suffix="px" theme={theme} />
                            <NumberInput label="Axis / Labels" value={fs.axis_label} onChange={v => setFs("axis_label", v)} min={8} max={18} suffix="px" theme={theme} />
                            <NumberInput label="Legend" value={fs.legend} onChange={v => setFs("legend", v)} min={8} max={18} suffix="px" theme={theme} />
                            <NumberInput label="Body Text" value={fs.body} onChange={v => setFs("body", v)} min={10} max={20} suffix="px" theme={theme} />
                            <NumberInput label="Footer" value={fs.footer} onChange={v => setFs("footer", v)} min={8} max={18} suffix="px" theme={theme} />
                            <NumberInput label="Trend Annotation" value={fs.trend_annotation} onChange={v => setFs("trend_annotation", v)} min={8} max={18} suffix="px" theme={theme} />
                        </div>
                    </Card>

                    {/* ── Brand Colors ── */}
                    <Card title="Brand Colors" theme={theme}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                            <ColorPicker label="Primary" value={b.primary_color} onChange={v => set({ primary_color: v })} theme={theme} />
                            <ColorPicker label="Secondary" value={b.secondary_color} onChange={v => set({ secondary_color: v })} theme={theme} />
                            <ColorPicker label="Accent" value={b.accent_color} onChange={v => set({ accent_color: v })} theme={theme} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginTop: 12 }}>
                            <ColorPicker label="Background" value={b.bg_color} onChange={v => set({ bg_color: v })} theme={theme} />
                            <ColorPicker label="Text" value={b.text_color} onChange={v => set({ text_color: v })} theme={theme} />
                            <ColorPicker label="Muted Text" value={b.muted_color} onChange={v => set({ muted_color: v })} theme={theme} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginTop: 12 }}>
                            <ColorPicker label="Card Background" value={b.card_bg} onChange={v => set({ card_bg: v })} theme={theme} />
                            <ColorPicker label="Card Border" value={b.card_border} onChange={v => set({ card_border: v })} theme={theme} />
                        </div>
                    </Card>

                    {/* ── Layout ── */}
                    <Card title="Layout Options" theme={theme}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                            <SelectInput label="Header Style" value={b.header_style}
                                         onChange={v => set({ header_style: v })}
                                         options={[{ v: "underline", l: "Underline" }, { v: "badge", l: "Badge" }, { v: "plain", l: "Plain" }]}
                                         theme={theme} />
                            <SelectInput label="Footer Position" value={b.footer_position}
                                         onChange={v => set({ footer_position: v })}
                                         options={[
                                             { v: "bottom-left", l: "Bottom Left" }, { v: "bottom-center", l: "Bottom Center" },
                                             { v: "bottom-right", l: "Bottom Right" }, { v: "none", l: "None" },
                                         ]} theme={theme} />
                            <SelectInput label="Show Cover Page" value={b.show_cover === false ? "no" : "yes"}
                                         onChange={v => set({ show_cover: v === "yes" })}
                                         options={[{ v: "yes", l: "Yes" }, { v: "no", l: "No" }]}
                                         theme={theme} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 12 }}>
                            <SelectInput label="Enable Table of Contents" value={b.enable_table_of_contents === false ? "no" : "yes"}
                                         onChange={v => set({ enable_table_of_contents: v === "yes" })}
                                         options={[{ v: "yes", l: "Yes" }, { v: "no", l: "No" }]}
                                         theme={theme} />
                        </div>
                    </Card>

                    {/* ── Reporting Settings ── */}
                    <Card title="Reporting Settings" theme={theme}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <div>
                                <Label theme={theme}>Financial Year Start Month</Label>
                                <NumberInput
                                    label=""
                                    value={b.financial_year_start_month ?? 3}
                                    onChange={v => set({ financial_year_start_month: Math.min(12, Math.max(1, v)) })}
                                    min={1} max={12} suffix=""
                                    theme={theme}
                                />
                                <span style={{ fontSize: 11, color: theme.textSecondary }}>
                                    1 = Jan, 2 = Feb, 3 = Mar … 12 = Dec
                                </span>
                            </div>
                        </div>
                    </Card>

                    {/* ── Chart Palette ── */}
                    <Card title="Chart Color Palette" theme={theme}>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
                            {b.chart_palette.map((color: string, idx: number) => (
                                <div key={idx} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                                    <input type="color" value={color} onChange={e => {
                                        const next = [...b.chart_palette]; next[idx] = e.target.value;
                                        set({ chart_palette: next });
                                    }} style={{ width: 48, height: 36, border: `1px solid ${theme.border}`, borderRadius: 6, cursor: "pointer" }} />
                                    <span style={{ fontSize: 10, color: theme.textSecondary }}>{idx + 1}</span>
                                </div>
                            ))}
                            <button onClick={() => set({ chart_palette: [...b.chart_palette, "#6366f1"] })}
                                    style={{
                                        width: 48, height: 36, borderRadius: 6, cursor: "pointer",
                                        border: `2px dashed ${theme.border}`, background: "none",
                                        color: theme.textSecondary, fontSize: 18, display: "flex",
                                        alignItems: "center", justifyContent: "center",
                                    }} title="Add color">+</button>
                        </div>
                        {b.chart_palette.length > 3 && (
                            <button onClick={() => set({ chart_palette: b.chart_palette.slice(0, -1) })}
                                    style={{
                                        marginTop: 8, background: "none", border: "none", fontSize: 12,
                                        color: theme.textSecondary, cursor: "pointer", padding: 0, textDecoration: "underline",
                                    }}>Remove last color</button>
                        )}
                    </Card>

                    {/* ── Entity Colors ── */}
                    <Card title="Entity Colors" subtitle="Assign fixed colors to specific values (e.g. products, customers) for consistent chart rendering"
                          theme={theme} defaultOpen={false}>
                        <EntityColorSection branding={b} onChange={onChange} theme={theme} />
                    </Card>

                </div>

                {/* ═══ Right — Live Preview ═══ */}
                <div style={{
                    width: previewExpanded ? 680 : 340, flexShrink: 0,
                    position: "sticky", top: 80,
                    display: "flex", flexDirection: "column", gap: 10,
                    transition: "width 0.25s ease",
                }}>
                    {/* Header */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{
                            fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                            letterSpacing: ".08em", color: theme.textSecondary,
                        }}>Preview</span>
                        <button onClick={() => setPreviewExpanded(!previewExpanded)}
                                style={{
                                    background: "none", border: `1px solid ${theme.border}`, borderRadius: 6,
                                    padding: "3px 8px", fontSize: 11, color: theme.textSecondary,
                                    cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
                                }}>
                            {previewExpanded ? "◀ Collapse" : "Expand ▶"}
                        </button>
                    </div>

                    {/* Cover Page */}
                    <PreviewThumbnail label="Cover Page" theme={theme} onExpand={() => setFullscreenPage("cover")}>
                        <CoverPagePreview branding={b} reportName={reportName} />
                    </PreviewThumbnail>

                    {/* Section Divider */}
                    <PreviewThumbnail label="Section Divider" theme={theme} onExpand={() => setFullscreenPage("divider")}>
                        <SectionDividerPreview branding={b} />
                    </PreviewThumbnail>

                    {/* Content Page */}
                    <PreviewThumbnail label="Content Page" theme={theme} onExpand={() => setFullscreenPage("content")}>
                        <ContentPagePreview branding={b} />
                    </PreviewThumbnail>
                </div>
            </div>

            {/* Fullscreen overlay */}
            {fullscreenPage && (
                <div onClick={() => setFullscreenPage(null)}
                     style={{
                         position: "fixed", inset: 0, zIndex: 9999,
                         background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)",
                         display: "flex", alignItems: "center", justifyContent: "center",
                         padding: 32, cursor: "zoom-out",
                     }}>
                    <div onClick={e => e.stopPropagation()}
                         style={{
                             width: "90vw", maxWidth: 1200,
                             borderRadius: 10, overflow: "hidden",
                             boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
                             cursor: "default",
                         }}>
                        {fullscreenPage === "cover" && <CoverPagePreview branding={b} reportName={reportName} />}
                        {fullscreenPage === "divider" && <SectionDividerPreview branding={b} />}
                        {fullscreenPage === "content" && <ContentPagePreview branding={b} />}
                    </div>
                    <button onClick={() => setFullscreenPage(null)}
                            style={{
                                position: "absolute", top: 16, right: 24,
                                background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8,
                                padding: "8px 14px", color: "#fff", fontSize: 14,
                                cursor: "pointer", fontWeight: 600,
                            }}>✕ Close</button>
                </div>
            )}
        </div>
    );
}